import cx from "clsx";
import ButtonTransparentV2 from "kreate-common/modules/kreate-ui/components/ButtonTransparentV2";
import Flex from "kreate-common/modules/kreate-ui/components/Flex";
import React from "react";
import {
  MdAdd,
  MdBallot,
  MdKeyboardArrowLeft,
  MdKeyboardArrowRight,
  MdOutlineWallpaper,
  MdPermMedia,
  MdSell,
  MdShoppingCart,
  MdVisibilityOff,
  MdWysiwyg,
} from "react-icons/md";

import GalleryAlbumItem from "../../components/GalleryAlbumItem";
import NavigationItem from "../../components/NavigationItem";
import { useGallerySidebarApi } from "../../hooks/useGallerySidebarApi";

import styles from "./index.module.scss";

import {
  formatInteger$Compact,
  formatInteger$Standard,
} from "@/modules/common-utils/format";
import { EM_DASH } from "@/modules/common-utils/unicode";
import { getShortenedAddress } from "@/modules/kreate-components/components/AddressViewer/utils";
import {
  getLinkToPageGalleryCollector$AllAssets,
  getLinkToPageGalleryCollector$ForSale,
  getLinkToPageGalleryCollector$HiddenAssets,
  getLinkToPageGalleryCollector$ReceivedOffers,
  getLinkToPageGalleryCollector$SentOffers,
  getLinkToPageGalleryCollector$UnsortedAssets,
} from "@/modules/navigation/utils/getLinkTo";

const VARIANT = {
  collapsed: styles.collapsed,
  expanded: styles.expanded,
};

type Props = {
  className?: string;
  style?: React.CSSProperties;
  variant: keyof typeof VARIANT;
  api: ReturnType<typeof useGallerySidebarApi>;
  toggleCollapse?: () => void;
};

export default function Content({
  className,
  style,
  variant,
  api,
  toggleCollapse,
}: Props) {
  const galleryInfo = api.params.galleryCollector?.populated?.galleryInfo;
  const addressIsh = api.params.addressIsh;
  const title =
    galleryInfo?.title ||
    (!addressIsh
      ? undefined
      : addressIsh.startsWith("$")
      ? addressIsh
      : getShortenedAddress(addressIsh, {
          prefixLength: 8,
          suffixLength: 8,
        })) ||
    EM_DASH;

  // TODO: @kien-kreate: show badges indicating the number
  return (
    <div
      className={cx(styles.container, className, VARIANT[variant])}
      style={style}
    >
      <Flex.Col>
        <Flex.Row
          gap="12px"
          padding="20px 24px"
          alignItems="center"
          justifyContent="space-around"
        >
          <Flex.Row flex="0 0 auto">
            <MdBallot size="24px" className={styles.colorFgDimmed} />
          </Flex.Row>
          <Flex.Cell
            className={cx(
              styles.textEllipsis,
              styles.lineHeight125,
              styles.hiddenIfCollapsed
            )}
            flex="1 1 auto"
            minWidth="0"
          >
            <span title={title}>{title}</span>
          </Flex.Cell>
          <Flex.Row>
            <ButtonTransparentV2
              icon={
                variant === "expanded" ? (
                  <MdKeyboardArrowLeft size="24px" />
                ) : (
                  <MdKeyboardArrowRight size="24px" />
                )
              }
              onClick={toggleCollapse}
            />
          </Flex.Row>
        </Flex.Row>
        <hr className={styles.divider} />
        <Flex.Col padding="12px 0">
          <NavigationItem
            active={api.params.sidebarLocation.type === "all-assets"}
            href={
              addressIsh
                ? getLinkToPageGalleryCollector$AllAssets(addressIsh)
                : undefined
            }
            icon={<MdOutlineWallpaper />}
            content={`All Assets (${formatInteger$Compact(
              api.params.galleryCollector?.totalNumArtworks
            )})`}
            title={
              api.params.galleryCollector?.totalNumArtworks
                ? formatInteger$Standard(
                    api.params.galleryCollector?.totalNumArtworks
                  )
                : undefined
            }
            collapsed={variant === "collapsed"}
          />
          <NavigationItem
            active={api.params.sidebarLocation.type === "for-sale"}
            href={
              addressIsh
                ? getLinkToPageGalleryCollector$ForSale(addressIsh)
                : undefined
            }
            icon={<MdShoppingCart />}
            content={`For Sale (${formatInteger$Compact(
              api.params.galleryCollector?.totalNumForSaleArtworks
            )})`}
            title={formatInteger$Standard(
              api.params.galleryCollector?.totalNumForSaleArtworks
            )}
            collapsed={variant === "collapsed"}
          />
          <NavigationItem
            active={api.params.sidebarLocation.type === "sent-offers"}
            href={
              addressIsh
                ? getLinkToPageGalleryCollector$SentOffers(addressIsh)
                : undefined
            }
            icon={<MdSell />}
            content={
              <span
                title={formatInteger$Standard(
                  api.params.galleryCollector?.totalNumSentOffers
                )}
              >{`Sent Offers (${formatInteger$Compact(
                api.params.galleryCollector?.totalNumSentOffers
              )})`}</span>
            }
            collapsed={variant === "collapsed"}
          />
          <NavigationItem
            active={api.params.sidebarLocation.type === "received-offers"}
            href={
              addressIsh
                ? getLinkToPageGalleryCollector$ReceivedOffers(addressIsh)
                : undefined
            }
            icon={<MdSell />}
            content={
              <span
                title={formatInteger$Standard(
                  api.params.galleryCollector?.totalNumReceivedOffers
                )}
              >{`Received Offers (${formatInteger$Compact(
                api.params.galleryCollector?.totalNumReceivedOffers
              )})`}</span>
            }
            collapsed={variant === "collapsed"}
          />
          {api.isLoggedInUserGalleryOwner ? (
            <NavigationItem
              active={api.params.sidebarLocation.type === "unsorted-assets"}
              href={
                addressIsh
                  ? getLinkToPageGalleryCollector$UnsortedAssets(addressIsh)
                  : undefined
              }
              icon={<MdWysiwyg />}
              content={`Unsorted Assets (${formatInteger$Compact(
                api.params.galleryCollector?.totalNumUnsortedArtworks
              )})`}
              title={
                api.params.galleryCollector?.totalNumUnsortedArtworks
                  ? formatInteger$Standard(
                      api.params.galleryCollector?.totalNumUnsortedArtworks
                    )
                  : undefined
              }
              collapsed={variant === "collapsed"}
            />
          ) : undefined}
          {api.isLoggedInUserGalleryOwner ? (
            <NavigationItem
              active={api.params.sidebarLocation.type === "hidden-assets"}
              href={
                addressIsh
                  ? getLinkToPageGalleryCollector$HiddenAssets(addressIsh)
                  : undefined
              }
              icon={<MdVisibilityOff />}
              title={
                api.params.galleryCollector?.totalNumHiddenArtworks
                  ? formatInteger$Standard(
                      api.params.galleryCollector?.totalNumHiddenArtworks
                    )
                  : undefined
              }
              content={`Hidden Assets (${formatInteger$Compact(
                api.params.galleryCollector?.totalNumHiddenArtworks
              )})`}
              collapsed={variant === "collapsed"}
            />
          ) : undefined}
        </Flex.Col>
        <hr className={styles.divider} />
        <Flex.Col padding="20px 0" gap="20px">
          <Flex.Row
            gap="12px"
            padding="0 24px"
            alignItems="center"
            justifyContent="space-around"
          >
            <Flex.Row flex="0 0 auto">
              <MdPermMedia size="24px" className={styles.colorFgDimmed} />
            </Flex.Row>
            <Flex.Cell
              className={cx(
                styles.textEllipsis,
                styles.lineHeight125,
                styles.hiddenIfCollapsed
              )}
              flex="1 1 auto"
              minWidth="0"
            >
              {"Exhibits"}
            </Flex.Cell>
            {api.isLoggedInUserGalleryOwner ? (
              <ButtonTransparentV2
                icon={<MdAdd />}
                onClick={async () => await api.createAlbum?.()}
                content={variant !== "collapsed" ? "Add" : undefined}
                disabled={api.busy}
              />
            ) : undefined}
          </Flex.Row>
          <Flex.Col>
            {(api.galleryAlbums || []).map((album) => (
              <GalleryAlbumItem
                key={album.galleryAlbumId}
                album={album}
                api={api}
                collapsed={variant === "collapsed"}
              />
            ))}
          </Flex.Col>
        </Flex.Col>
      </Flex.Col>
    </div>
  );
}
